'use client'

import { useQuery } from '@apollo/client/react'
import { LDClient, LDEvaluationDetail, basicLogger, initialize } from 'launchdarkly-js-client-sdk'
import { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog'
import { reportClientError } from '../_components/chrome/scripts/DataDogRumScript'
import { createLaunchDarklyContext } from '../_config/Experimentation.config'

import { useAuthentication } from './AuthenticationProvider.client'
import { useGraphqlClients } from './GraphqlClientsProvider.client'
import { useLocalization } from './LocalizationProvider.client'
import { useSession } from './SessionProvider.client'
import { useTrackFeatureFlagDecision } from './TrackingProvider.client'

export const useExperimentationContext = () => {
  const { account } = useAuthentication()
  const { personalizedClient } = useGraphqlClients()
  const localizationAttributes = useLocalization()
  const { attributes } = useSession()

  const { data: organizationData } = useQuery(
    gql(`
      query GetOrganizationInfoForLaunchDarklyContext {
        my {
          id
          portal {
            id
            name
          }
          activeTeamStore {
            id
            name
            organization {
              id
              name
            }
          }
        }
      }
    `),
    {
      client: personalizedClient,
      skip: !account,
    }
  )
  return useMemo(() => {
    return createLaunchDarklyContext({
      account,
      sessionAttributes: attributes,
      localizationAttributes,
      portal: organizationData?.my.portal ?? undefined,
      activeTeamStore: organizationData?.my.activeTeamStore ?? undefined,
    })
  }, [
    account,
    attributes,
    localizationAttributes,
    organizationData?.my.activeTeamStore,
    organizationData?.my.portal,
  ])
}

const createClient = ({
  context,
  initialState,
  isBot,
}: {
  context: ReturnType<typeof createLaunchDarklyContext>
  initialState: Record<PropertyKey, unknown> | undefined
  isBot: boolean
}) => {
  if (typeof window === 'undefined') return undefined
  if (!process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_API_KEY) return undefined
  // Bots are not filtered automatically in LaunchDarkly
  if (isBot) return undefined

  const client = initialize(process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_API_KEY, context, {
    streaming: true,
    streamReconnectDelay: 3000,
    sendEvents: true,
    sendEventsOnlyForVariation: true,
    bootstrap: initialState,
    logger:
      process.env.NEXT_PUBLIC_FIGS_ENV === 'prod' ? basicLogger({ level: 'none' }) : undefined,
    inspectors: [
      {
        type: 'flag-used',
        name: 'dd-inspector',
        method: (flagKey: string, detail: LDEvaluationDetail) => {
          // DD does not support: . , : + - = && || > < ! ( ) { } [ ] ^ " ~ * ? \
          const sanitizedKey = flagKey.replaceAll(/[.,\-:+&|=><!()\[\]{}\^"~*?\\]/g, '_')
          window.DD_RUM?.onReady?.(function () {
            window.DD_RUM.addFeatureFlagEvaluation(sanitizedKey, detail.value)
          })
        },
      },
    ],
  })

  return client
}

const overridesAllowed = process.env.NEXT_PUBLIC_FIGS_ENV !== 'prod'

type UseClientProps = {
  initialState: Record<PropertyKey, unknown> | undefined
  featureFlagOverrides: Record<string, string> | undefined
}

const useClientImpl = (
  { initialState, featureFlagOverrides }: UseClientProps = {
    initialState: undefined,
    featureFlagOverrides: undefined,
  }
) => {
  const session = useSession()
  const context = useExperimentationContext()
  const [_, startTransition] = useTransition()

  const lastContextUsedRef = useRef(context)

  const clientRef = useRef<LDClient | undefined>(undefined)

  if (!clientRef.current) {
    clientRef.current = createClient({
      context,
      initialState,
      isBot: session.attributes.isBot,
    })
    lastContextUsedRef.current = context
  }

  if (clientRef.current && context !== lastContextUsedRef.current) {
    clientRef.current.identify(context)
    lastContextUsedRef.current = context
  }

  const [clientStatus, setClientStatus] = useState<'pending' | 'resolved' | 'rejected'>(() => {
    // Derived only from inputs the server and browser share, so hydration matches. The browser builds a
    // client exactly when the key is set and the visitor isn't a bot, so `'resolved'` always has one.
    if (session.attributes.isBot || !process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_API_KEY)
      return 'rejected'
    if (initialState) return 'resolved'
    return 'pending'
  })

  const internalsRef = useRef<{
    disabledFeatures: Record<string, true>
  }>({
    disabledFeatures: {},
  })

  // On cached pages the server can wrongly flag a visitor as a bot. If the browser disagrees after hydration, undo the
  // rejection so the client initializes.
  const [lastIsBot, setLastIsBot] = useState(session.attributes.isBot)
  if (lastIsBot !== session.attributes.isBot) {
    setLastIsBot(session.attributes.isBot)
    if (!session.attributes.isBot && clientStatus === 'rejected' && clientRef.current) {
      internalsRef.current.disabledFeatures = {}
      setClientStatus('pending')
    }
  }

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (clientStatus !== 'pending') return

    clientRef.current
      ?.waitForInitialization(3) // Bail/error at 3 seconds of waiting.
      .then(() => {
        // I know this is sketchy... but it shouldn't cause any bugs or issues.
        // We don't have a precise way to know the entire page has gone through its initial hydration/render.
        // In the page's directory, we need the initial renders to run and check their flags prior to the client being "ready".
        // The client is not primed with `initialState` due to these pages being cached/shared despite `initialState` being user specific.
        // Without this, if the client gets ready too quick, we can have CLS in pages directory due to SSR being the control flag and clientside being the user's evaluation.
        // Once we are fully off of cached HTML, we can completely rip out everything related to `ifAccessedPriorToDecisionInitialization` and `isClientReady` and just require `initialState` always.

        // Wait a time amount that is similar to how long it took content to appear.
        setTimeout(() => {
          // Deprioritize this React update.
          startTransition(() => {
            // Defer until browser is ready for a visual update.
            requestAnimationFrame(() => {
              // Now assume we have done an initial render for page sections above the fold.
              setClientStatus('resolved')
            })
          })
        }, 1500)
      })
      .catch(error => {
        setClientStatus('rejected')
        reportClientError({
          error: new Error(`Failed to load LaunchDarkly client within 3 seconds.`, {
            cause: error,
          }),
          context: {
            scope: 'ExperimentationProvider',
            label: 'LaunchDarkly client connection failed.',
          },
        })
      })
  }, [clientStatus])

  return {
    client: clientStatus === 'resolved' ? clientRef.current : undefined,
    initialFlags: initialState,
    featureFlagOverrides: overridesAllowed ? featureFlagOverrides : undefined,
    clientStatus,
    _internals: internalsRef.current,
  }
}

const ExperimentationContainer = createContainer(useClientImpl)
ExperimentationContainer.Provider.displayName = 'ExperimentationProviderImpl'

export const ExperimentationProvider = ({
  children,
  ...props
}: UseClientProps & { children?: React.ReactNode }) => {
  return (
    <ExperimentationContainer.Provider initialState={props}>
      {children}
    </ExperimentationContainer.Provider>
  )
}

export function useFeatureFlag<T extends string>(options: {
  key: string
  controlVariant?: T
  ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant'
  skip?: boolean
}): T

export function useFeatureFlag<T extends string>(options: {
  key: string
  controlVariant?: T
  ifAccessedPriorToDecisionInitialization: 'return-null-while-pending'
  skip?: boolean
}): null | T

export function useFeatureFlag<T extends string>({
  key,
  controlVariant = 'control',
  ifAccessedPriorToDecisionInitialization,
  skip,
}: {
  key: string
  controlVariant?: T | string
  ifAccessedPriorToDecisionInitialization:
    | 'return-null-while-pending'
    | 'lock-pageview-to-controlVariant'
  skip?: boolean
}) {
  const { client, clientStatus, initialFlags, featureFlagOverrides, _internals } =
    ExperimentationContainer.useContainer()
  const { trackFeatureFlagDecision } = useTrackFeatureFlagDecision()

  // Bail with control if we are skipping.
  if (skip === true) {
    return controlVariant
  }

  // Non-prod testing override, inert in production, see FeatureFlagOverrides.server.ts.
  const override = featureFlagOverrides?.[key]
  if (override !== undefined) {
    return override
  }

  // Lock to controls if we failed to load Launch Darkly completely
  if (clientStatus === 'rejected') {
    _internals.disabledFeatures[key] = true
  }

  // Lock to control if pending initialization and developer chose to lock on pending access
  if (
    clientStatus === 'pending' &&
    ifAccessedPriorToDecisionInitialization === 'lock-pageview-to-controlVariant'
  ) {
    _internals.disabledFeatures[key] = true
  }

  // If locked, bail with control.
  if (_internals.disabledFeatures[key]) {
    return controlVariant
  }

  // We are unlocked, but we have no ability to give a decision, so use control or pending state depending on input.
  if (!client && !initialFlags) {
    if (ifAccessedPriorToDecisionInitialization === 'lock-pageview-to-controlVariant') {
      return controlVariant
    } else {
      return null
    }
  }

  // We can make a decision, so make one. Calling `client.variation` will track this decision in Launch Darkly,
  // so we need to follow up and track it in other things like our custom `trackFeatureFlagDecision` call
  const decision = client
    ? `${client.variation(key, controlVariant)}`
    : initialFlags?.[key]
    ? `${initialFlags[key]}`
    : controlVariant

  trackFeatureFlagDecision({
    key,
    decision,
  })

  return decision
}
